import { costPostures } from '@finnest/pulumi-cost-posture';
import { Effect, Schema, SchemaGetter, SchemaIssue, SchemaTransformation } from 'effect';
import { OpinParticipantRole } from '../../../opin/participant-role/participant-role.ts';
import { dual } from 'effect/Function';

export const SliceText = Schema.Trim.check(Schema.isNonEmpty());
export type SliceText = typeof SliceText.Type;

export const SliceFlag = Schema.Union([
  Schema.Boolean,
  Schema.Literals(['true', 'false']).pipe(
    Schema.decodeTo(Schema.Boolean, {
      decode: SchemaGetter.transform((value) => value === 'true'),
      encode: SchemaGetter.transform((value) => (value ? 'true' : 'false')),
    }),
  ),
]).annotate({
  identifier: 'runtime-cd.deploy.slice-flag',
  title: 'SliceFlag',
  description: 'Boolean deploy-slice flag, including the Pulumi string true/false wire form.',
});
export type SliceFlag = typeof SliceFlag.Type;

const DECIMAL_INTEGER = /^-?\d+$/u;

export const SliceInt = Schema.Union([
  Schema.Int,
  Schema.Trim.check(Schema.isPattern(DECIMAL_INTEGER)).pipe(
    Schema.decodeTo(Schema.Int, {
      decode: SchemaGetter.transform((digits) => Number(digits)),
      encode: SchemaGetter.transform((value) => String(value)),
    }),
  ),
]).annotate({
  identifier: 'runtime-cd.deploy.slice-int',
  title: 'SliceInt',
  description: 'Integer deploy-slice field, including the Pulumi decimal-string wire form.',
});
export type SliceInt = typeof SliceInt.Type;

export const SliceNumber = Schema.Union([
  Schema.Finite,
  Schema.Trim.check(Schema.isNonEmpty()).pipe(
    Schema.decodeTo(Schema.Finite, {
      decode: SchemaGetter.transform((digits) => Number(digits)),
      encode: SchemaGetter.transform((value) => String(value)),
    }),
  ),
]).annotate({
  identifier: 'runtime-cd.deploy.slice-number',
  title: 'SliceNumber',
  description: 'Numeric deploy-slice field, including the Pulumi decimal-string wire form.',
});
export type SliceNumber = typeof SliceNumber.Type;

// Decoding the text and validating it are two separate failures and are reported as such: text
// that never became JSON has no nested key to point at, so the overlay names itself with its own
// message, while anything the schema rejects keeps its nested path so the operator sees which key
// failed.
const JSON_TEXT = SchemaTransformation.fromJsonString();

function overlayJsonText(message: string): SchemaGetter.Getter<unknown, string> {
  return SchemaGetter.transformOptionalEffect((text, options) =>
    SchemaGetter.run(JSON_TEXT.decode, text, options).pipe(
      Effect.mapError(() => new SchemaIssue.InvalidValue({ message })),
    ),
  );
}

/** The JSON text Pulumi stores for a nested overlay, decoded into the overlay's own schema. */
export type SliceJsonText<S extends Schema.Top> = Schema.compose<
  S,
  Schema.decodeTo<Schema.Unknown, Schema.String>
>;

const sliceJsonText: {
  (message: string): <S extends Schema.Top>(schema: S) => SliceJsonText<S>;
  <S extends Schema.Top>(schema: S, message: string): SliceJsonText<S>;
} = /* @__PURE__ */ dual(2, <S extends Schema.Top>(schema: S, message: string): SliceJsonText<S> =>
  Schema.String.pipe(
    Schema.decodeTo(Schema.Unknown, {
      decode: overlayJsonText(message),
      encode: SchemaGetter.stringifyJson(),
    }),
    Schema.decodeTo(schema),
  ),
);

/** A nested JSON overlay arrives either as the decoded value or as the JSON text Pulumi stores. */
export type SliceJsonOverlay<S extends Schema.Top> = Schema.Union<readonly [S, SliceJsonText<S>]>;

export const sliceJsonOverlay: {
  (message: string): <S extends Schema.Top>(schema: S) => SliceJsonOverlay<S>;
  <S extends Schema.Top>(schema: S, message: string): SliceJsonOverlay<S>;
} = /* @__PURE__ */ dual(
  2,
  <S extends Schema.Top>(schema: S, message: string): SliceJsonOverlay<S> =>
    Schema.Union([schema, sliceJsonText(schema, message)]),
);

const SliceTextListArray = Schema.Array(SliceText);

export const SliceTextList = sliceJsonOverlay(
  SliceTextListArray,
  'must be a JSON array of non-empty text',
).annotate({
  identifier: 'runtime-cd.deploy.slice-text-list',
  title: 'SliceTextList',
  description:
    'List of non-empty deploy-slice text fields, including a JSON-array Pulumi wire form.',
});
export type SliceTextList = typeof SliceTextList.Type;

export const PLATFORM_ENVIRONMENTS = ['dev', 'sandbox', 'prod'] as const;
export const SliceEnvironment = Schema.Literals(PLATFORM_ENVIRONMENTS).annotate({
  title: 'SliceEnvironment',
  description: 'Closed platform environment vocabulary on a platform deploy slice.',
});
export type SliceEnvironment = typeof SliceEnvironment.Type;

export const CostPosture = Schema.Literals(costPostures);
export type CostPosture = typeof CostPosture.Type;

/** How a tripped monthly budget locks spend (ADR-071); there is no `off`. */
export const COST_HARD_CAP_MODES = ['freeze', 'stop'] as const;
export const CostHardCapMode = Schema.Literals(COST_HARD_CAP_MODES).annotate({
  title: 'CostHardCapMode',
  description: 'Hard-cap lock: freeze denies capacity growth, stop also scales the fleet down.',
});
export type CostHardCapMode = typeof CostHardCapMode.Type;

/**
 * The cap trips at 90 % of ACTUAL spend: billing data lags by hours, so a 100 % trigger lets spend
 * run past the cap before the lock lands. FORECASTED notifications keep warning ahead of it.
 */
export const COST_HARD_CAP_TRIGGER_PERCENT = 90;

// The slice's role field IS the participant-role vocabulary, not a parallel copy of it.
export const SliceOpinRole = OpinParticipantRole;
export type SliceOpinRole = OpinParticipantRole;

const CloudflareDnsDelegationFields = Schema.Struct({
  enabled: SliceFlag,
  accountId: Schema.optionalKey(SliceText),
  delegatedZoneName: Schema.optionalKey(SliceText),
  parentZoneName: Schema.optionalKey(SliceText),
  ttl: Schema.optionalKey(SliceInt),
});

export const CloudflareDnsDelegation = sliceJsonOverlay(
  CloudflareDnsDelegationFields,
  'cloudflareDnsDelegation is not a JSON object',
).annotate({
  identifier: 'runtime-cd.deploy.cloudflare-dns-delegation',
  title: 'CloudflareDnsDelegation',
  description: 'Cloudflare parent-zone delegation on a platform deploy slice.',
});
export type CloudflareDnsDelegation = typeof CloudflareDnsDelegation.Type;

export const DnsDelegationMode = Schema.Literals(['auto', 'manual']);
export type DnsDelegationMode = typeof DnsDelegationMode.Type;

const BudgetUsdMapFields = Schema.Record(Schema.String, SliceNumber);

export const SliceBudgetUsdMap = sliceJsonOverlay(
  BudgetUsdMapFields,
  'monthlyCostBudgetsUsd is not a JSON object of finite USD amounts',
).annotate({
  identifier: 'runtime-cd.deploy.slice-budget-usd-map',
  title: 'SliceBudgetUsdMap',
  description: 'Per-stack monthly cost budget map, including a JSON-object Pulumi wire form.',
});
export type SliceBudgetUsdMap = typeof SliceBudgetUsdMap.Type;

const CAPACITY_TYPES = ['spot', 'on-demand'] as const;

const SliceDuration = SliceText.check(
  Schema.isPattern(/^(?:Never|\d+(?:s|m|h))$/u, {
    message: 'expected a Karpenter duration like "5m", "720h", or "Never"',
  }),
);

const SliceQuantity = SliceText.check(
  Schema.isPattern(/^\d+(?:\.\d+)?(?:[EPTGMK]i?|m)?$/u, {
    message: 'expected a Kubernetes quantity like "32" or "64Gi"',
  }),
);

// Karpenter NodePool weights run from 1 to 100; the message and the bound share one constant.
const POOL_WEIGHT_MIN = 1;
const POOL_WEIGHT_MAX = 100;
const SlicePoolWeight = SliceInt.check(
  Schema.isBetween(
    { minimum: POOL_WEIGHT_MIN, maximum: POOL_WEIGHT_MAX },
    { message: `weight must be an integer from ${POOL_WEIGHT_MIN} to ${POOL_WEIGHT_MAX}` },
  ),
);

const SliceCapacityTypes = Schema.NonEmptyArray(Schema.Literals(CAPACITY_TYPES));

const SlicePoolOverride = Schema.Struct({
  capacityTypes: Schema.optionalKey(SliceCapacityTypes),
  consolidateAfter: Schema.optionalKey(SliceDuration),
  cpuLimit: Schema.optionalKey(SliceQuantity),
  expireAfter: Schema.optionalKey(SliceDuration),
  memoryLimit: Schema.optionalKey(SliceQuantity),
  weight: Schema.optionalKey(SlicePoolWeight),
});

// The AWS-only amd64 OPIN MOP gateway pool: its capacity types are its only operator knob.
const SliceMopOverride = Schema.Struct({ capacityTypes: SliceCapacityTypes });

const SlicePoolOverrides = {
  app: Schema.optionalKey(SlicePoolOverride),
  stateful: Schema.optionalKey(SlicePoolOverride),
  system: Schema.optionalKey(SlicePoolOverride),
};

export const SliceAutoscalingOverride = sliceJsonOverlay(
  Schema.Struct({ ...SlicePoolOverrides, mop: Schema.optionalKey(SliceMopOverride) }),
  'autoscalingOverride is not a JSON object of system/stateful/app/mop pool knobs',
).annotate({
  identifier: 'runtime-cd.deploy.slice-autoscaling-override',
  title: 'SliceAutoscalingOverride',
  description:
    'AWS operator autoscaling overlay for system/stateful/app pools and the mop pool capacity types, including a JSON-object Pulumi wire form.',
});
export type SliceAutoscalingOverride = typeof SliceAutoscalingOverride.Type;

// Azure has no mop pool (the MOP gateway pool is AWS-only), so a mop knob is refused by name
// instead of being silently ignored.
const AzureMopRefused = Schema.optionalKey(
  Schema.Never.annotate({
    message: 'mop is an AWS-only pool; remove autoscalingOverride.mop from the Azure deployment',
  }),
);

export const AzureSliceAutoscalingOverride = sliceJsonOverlay(
  Schema.Struct({ ...SlicePoolOverrides, mop: AzureMopRefused }),
  'autoscalingOverride is not a JSON object of system/stateful/app pool knobs',
).annotate({
  identifier: 'runtime-cd.deploy.azure-slice-autoscaling-override',
  title: 'AzureSliceAutoscalingOverride',
  description:
    'Azure operator autoscaling overlay for system/stateful/app pools, including a JSON-object Pulumi wire form; the AWS-only mop pool is refused.',
});
export type AzureSliceAutoscalingOverride = typeof AzureSliceAutoscalingOverride.Type;
